/**
 * Auth store for Telegram Mini App
 * 
 * Flow:
 * 1. Get initData from Telegram WebApp
 * 2. Send to AUTH Service /api/v1/telegram/mini-app/init
 * 3. Receive access_token + issuer
 * 4. Store in memory and use for API requests
 */

interface AuthState {
  accessToken: string | null;
  accessTokenExpiresAt: number | null;
  refreshToken: string | null;
  issuer: string | null;
  userId: string | null;
  orgId: string | null;
  telegramId: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface MiniAppInitResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  user_id: string;
  org_id: string;
  telegram_id: number;
  issuer: string;
  is_new_user: boolean;
}

const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_URL || '/auth-api';

let authState: AuthState = {
  accessToken: null,
  accessTokenExpiresAt: null,
  refreshToken: null,
  issuer: null,
  userId: null,
  orgId: null,
  telegramId: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

let storedInitData: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

const listeners: Set<() => void> = new Set();

function notifyListeners() {
  listeners.forEach(listener => listener());
}

export function subscribeToAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthState(): AuthState {
  return { ...authState };
}

export function getAccessToken(): string | null {
  return authState.accessToken;
}

export function isAccessTokenExpiringSoon(bufferSeconds = 30): boolean {
  if (!authState.accessToken || !authState.accessTokenExpiresAt) return true;
  const threshold = authState.accessTokenExpiresAt - Math.max(1, bufferSeconds) * 1000;
  return Date.now() >= threshold;
}

export function getIssuer(): string | null {
  return authState.issuer;
}

export function isAuthenticated(): boolean {
  return authState.isAuthenticated;
}

/**
 * Initialize authentication using Telegram Mini App initData
 */
export async function initializeAuth(initData: string): Promise<boolean> {
  storedInitData = initData;

  if (!initData) {
    authState = {
      ...authState,
      isLoading: false,
      error: 'No initData provided',
    };
    notifyListeners();
    return false;
  }

  authState = {
    ...authState,
    isLoading: true,
    error: null,
  };
  notifyListeners();

  try {
    const response = await fetch(`${AUTH_API_BASE}/api/v1/telegram/mini-app/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ init_data: initData }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    const data: MiniAppInitResponse = await response.json();

    authState = {
      accessToken: data.access_token,
      accessTokenExpiresAt: Date.now() + Math.max(1, data.expires_in) * 1000,
      refreshToken: data.refresh_token || null,
      issuer: data.issuer,
      userId: data.user_id,
      orgId: data.org_id,
      telegramId: data.telegram_id,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    };
    notifyListeners();

    console.info('[Auth] Initialized successfully', {
      userId: data.user_id,
      telegramId: data.telegram_id,
      isNewUser: data.is_new_user,
    });

    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Authentication failed';
    console.error('[Auth] Initialization failed:', message);
    
    authState = {
      ...authState,
      isLoading: false,
      error: message,
    };
    notifyListeners();
    
    return false;
  }
}

/**
 * Re-authenticate using stored initData (called on 401)
 */
export async function refreshAuth(): Promise<boolean> {
  if (!storedInitData) {
    console.warn('[Auth] No stored initData for refresh');
    return false;
  }
  if (refreshPromise) {
    return refreshPromise;
  }
  console.info('[Auth] Refreshing token...');
  refreshPromise = initializeAuth(storedInitData).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Ensure access token is fresh enough for API call.
 * Returns true if token is ready, false if refresh failed.
 */
export async function ensureFreshAuth(bufferSeconds = 30): Promise<boolean> {
  if (!authState.accessToken) return false;
  if (!isAccessTokenExpiringSoon(bufferSeconds)) return true;
  return refreshAuth();
}

export function clearAuth(): void {
  authState = {
    accessToken: null,
    accessTokenExpiresAt: null,
    refreshToken: null,
    issuer: null,
    userId: null,
    orgId: null,
    telegramId: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  };
  notifyListeners();
}
